import React, { useEffect, useState } from 'react';
import { SearchIcon, SlidersIcon, ChevronDownIcon, UserIcon, HeartIcon, XIcon, FilterIcon, SortAscIcon, SortDescIcon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
type Teammate = {
  id: string;
  name: string;
  photo: string;
  position: string;
  teams: {
    name: string;
    logo: string;
    seasons: string[];
  }[];
  minutesPlayed: number;
  nationality: string;
  nationalityFlag: string;
  competitions: {
    name: string;
    logo: string;
  }[];
  seasonsShared: string[];
};
type FilterState = {
  seasons: string[];
  teams: string[];
  positions: string[];
  nationalities: string[];
  competitions: string[];
  minutesRange: [number, number];
  sortBy: 'minutes' | 'seasons' | 'alphabetical';
  sortOrder: 'asc' | 'desc';
  search: string;
};
const PlayerTeammates = () => {
  const [filters, setFilters] = useState<FilterState>({
    seasons: [],
    teams: [],
    positions: [],
    nationalities: [],
    competitions: [],
    minutesRange: [0, 5000],
    sortBy: 'minutes',
    sortOrder: 'desc',
    search: ''
  });
  // Mock data for teammates
  const allTeammates: Teammate[] = [{
    id: '1',
    name: 'Anthony Davis',
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/203076.png',
    position: 'Power Forward',
    teams: [{
      name: 'Los Angeles Lakers',
      logo: 'https://cdn.nba.com/logos/nba/1610612747/global/L/logo.svg',
      seasons: ['2019-20', '2020-21', '2021-22', '2022-23', '2023-24']
    }],
    minutesPlayed: 4852,
    nationality: 'USA',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/us.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2019-20', '2020-21', '2021-22', '2022-23', '2023-24']
  }, {
    id: '2',
    name: 'Russell Westbrook',
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/201566.png',
    position: 'Point Guard',
    teams: [{
      name: 'Los Angeles Lakers',
      logo: 'https://cdn.nba.com/logos/nba/1610612747/global/L/logo.svg',
      seasons: ['2021-22', '2022-23']
    }],
    minutesPlayed: 2340,
    nationality: 'USA',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/us.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2021-22', '2022-23']
  }, {
    id: '3',
    name: 'Kyrie Irving',
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/202681.png',
    position: 'Point Guard',
    teams: [{
      name: 'Brooklyn Nets',
      logo: 'https://cdn.nba.com/logos/nba/1610612751/global/L/logo.svg',
      seasons: ['2019-20', '2020-21', '2021-22']
    }],
    minutesPlayed: 3150,
    nationality: 'USA',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/us.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2019-20', '2020-21', '2021-22']
  }, {
    id: '4',
    name: 'Kevin Durant',
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/201142.png',
    position: 'Small Forward',
    teams: [{
      name: 'Brooklyn Nets',
      logo: 'https://cdn.nba.com/logos/nba/1610612751/global/L/logo.svg',
      seasons: ['2019-20', '2020-21', '2021-22']
    }],
    minutesPlayed: 2980,
    nationality: 'USA',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/us.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2019-20', '2020-21', '2021-22']
  }, {
    id: '5',
    name: 'Austin Reaves',
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/1630559.png',
    position: 'Shooting Guard',
    teams: [{
      name: 'Los Angeles Lakers',
      logo: 'https://cdn.nba.com/logos/nba/1610612747/global/L/logo.svg',
      seasons: ['2021-22', '2022-23', '2023-24']
    }],
    minutesPlayed: 2455,
    nationality: 'USA',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/us.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2021-22', '2022-23', '2023-24']
  }, {
    id: '6',
    name: 'Jayson Tatum',
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/1628369.png',
    position: 'Small Forward',
    teams: [{
      name: 'Boston Celtics',
      logo: 'https://cdn.nba.com/logos/nba/1610612738/global/L/logo.svg',
      seasons: ['2017-18', '2018-19']
    }],
    minutesPlayed: 1820,
    nationality: 'USA',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/us.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2017-18', '2018-19']
  }, {
    id: '7',
    name: 'Jaylen Brown',
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/1627759.png',
    position: 'Shooting Guard',
    teams: [{
      name: 'Boston Celtics',
      logo: 'https://cdn.nba.com/logos/nba/1610612738/global/L/logo.svg',
      seasons: ['2017-18', '2018-19']
    }],
    minutesPlayed: 1755,
    nationality: 'USA',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/us.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2017-18', '2018-19']
  }, {
    id: '8',
    name: "D'Angelo Russell",
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/1626156.png',
    position: 'Point Guard',
    teams: [{
      name: 'Los Angeles Lakers',
      logo: 'https://cdn.nba.com/logos/nba/1610612747/global/L/logo.svg',
      seasons: ['2022-23', '2023-24']
    }],
    minutesPlayed: 1520,
    nationality: 'USA',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/us.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2022-23', '2023-24']
  }, {
    id: '9',
    name: 'Joe Harris',
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/203925.png',
    position: 'Shooting Guard',
    teams: [{
      name: 'Brooklyn Nets',
      logo: 'https://cdn.nba.com/logos/nba/1610612751/global/L/logo.svg',
      seasons: ['2019-20', '2020-21', '2021-22']
    }],
    minutesPlayed: 2850,
    nationality: 'USA',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/us.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2019-20', '2020-21', '2021-22']
  }, {
    id: '10',
    name: 'Nicolas Claxton',
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/1629651.png',
    position: 'Center',
    teams: [{
      name: 'Brooklyn Nets',
      logo: 'https://cdn.nba.com/logos/nba/1610612751/global/L/logo.svg',
      seasons: ['2019-20', '2020-21', '2021-22']
    }],
    minutesPlayed: 1950,
    nationality: 'USA',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/us.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2019-20', '2020-21', '2021-22']
  }, {
    id: '11',
    name: 'Al Horford',
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/201143.png',
    position: 'Center',
    teams: [{
      name: 'Boston Celtics',
      logo: 'https://cdn.nba.com/logos/nba/1610612738/global/L/logo.svg',
      seasons: ['2017-18', '2018-19']
    }],
    minutesPlayed: 1690,
    nationality: 'Dominican Republic',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/do.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2017-18', '2018-19']
  }, {
    id: '12',
    name: 'Goran Dragić',
    photo: 'https://cdn.nba.com/headshots/nba/latest/1040x760/201609.png',
    position: 'Point Guard',
    teams: [{
      name: 'Brooklyn Nets',
      logo: 'https://cdn.nba.com/logos/nba/1610612751/global/L/logo.svg',
      seasons: ['2021-22']
    }],
    minutesPlayed: 520,
    nationality: 'Slovenia',
    nationalityFlag: 'https://flagicons.lipis.dev/flags/4x3/si.svg',
    competitions: [{
      name: 'NBA',
      logo: 'https://cdn.nba.com/logos/leagues/logo-nba.svg'
    }],
    seasonsShared: ['2021-22']
  }];
  const [filteredTeammates, setFilteredTeammates] = useState<Teammate[]>(allTeammates);
  // Extract unique values for filter options
  const allSeasons = Array.from(new Set(allTeammates.flatMap(teammate => teammate.seasonsShared))).sort();
  const allTeams = Array.from(new Set(allTeammates.flatMap(teammate => teammate.teams.map(team => team.name)))).sort();
  const allPositions = Array.from(new Set(allTeammates.map(teammate => teammate.position))).sort();
  const allNationalities = Array.from(new Set(allTeammates.map(teammate => teammate.nationality))).sort();
  const allCompetitions = Array.from(new Set(allTeammates.flatMap(teammate => teammate.competitions.map(comp => comp.name)))).sort();
  // Maximum minutes for the range slider
  const maxMinutes = Math.max(...allTeammates.map(teammate => teammate.minutesPlayed));
  // Apply filters
  useEffect(() => {
    let results = allTeammates;
    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      results = results.filter(teammate => teammate.name.toLowerCase().includes(searchLower));
    }
    // Apply season filter
    if (filters.seasons.length > 0) {
      results = results.filter(teammate => teammate.seasonsShared.some(season => filters.seasons.includes(season)));
    }
    // Apply team filter
    if (filters.teams.length > 0) {
      results = results.filter(teammate => teammate.teams.some(team => filters.teams.includes(team.name)));
    }
    // Apply position filter
    if (filters.positions.length > 0) {
      results = results.filter(teammate => filters.positions.includes(teammate.position));
    }
    // Apply nationality filter
    if (filters.nationalities.length > 0) {
      results = results.filter(teammate => filters.nationalities.includes(teammate.nationality));
    }
    // Apply competition filter
    if (filters.competitions.length > 0) {
      results = results.filter(teammate => teammate.competitions.some(comp => filters.competitions.includes(comp.name)));
    }
    // Apply minutes range filter
    results = results.filter(teammate => teammate.minutesPlayed >= filters.minutesRange[0] && teammate.minutesPlayed <= filters.minutesRange[1]);
    // Apply sorting
    results = [...results].sort((a, b) => {
      if (filters.sortBy === 'minutes') {
        return filters.sortOrder === 'asc' ? a.minutesPlayed - b.minutesPlayed : b.minutesPlayed - a.minutesPlayed;
      } else if (filters.sortBy === 'seasons') {
        return filters.sortOrder === 'asc' ? a.seasonsShared.length - b.seasonsShared.length : b.seasonsShared.length - a.seasonsShared.length;
      } else {
        return filters.sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      }
    });
    setFilteredTeammates(results);
  }, [filters]);
  // Handle filter changes
  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };
  // Handle multi-select filters
  const toggleFilterItem = (key: keyof FilterState, item: string) => {
    setFilters(prev => {
      const currentItems = prev[key] as string[];
      return {
        ...prev,
        [key]: currentItems.includes(item) ? currentItems.filter(i => i !== item) : [...currentItems, item]
      };
    });
  };
  // Format minutes as hours and minutes
  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };
  // Get the position color
  const getPositionColor = (position: string) => {
    switch (position) {
      case 'Point Guard':
        return 'bg-blue-100 text-blue-800';
      case 'Shooting Guard':
        return 'bg-green-100 text-green-800';
      case 'Small Forward':
        return 'bg-yellow-100 text-yellow-800';
      case 'Power Forward':
        return 'bg-orange-100 text-orange-800';
      case 'Center':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Handle column header click for sorting
  const handleSortByColumn = (column: 'minutes' | 'seasons' | 'alphabetical') => {
    if (filters.sortBy === column) {
      // Toggle sort order if same column
      handleFilterChange('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Change column and set default sort order
      setFilters(prev => ({
        ...prev,
        sortBy: column,
        sortOrder: column === 'alphabetical' ? 'asc' : 'desc'
      }));
    }
  };
  // Get sort indicator for column header
  const getSortIndicator = (column: 'minutes' | 'seasons' | 'alphabetical') => {
    if (filters.sortBy !== column) return null;
    return filters.sortOrder === 'asc' ? <ArrowUpIcon className="w-3 h-3 ml-1" data-id="element-664" data-mp="i7uz8mbgpva0" /> : <ArrowDownIcon className="w-3 h-3 ml-1" data-id="element-665" data-mp="i50c2n12dwcw" />;
  };
  return <div className="bg-white p-6 rounded-lg shadow-md w-full" data-id="element-666" data-mp="n5ef1qnt6g42">
      <div className="flex justify-between items-center mb-5" data-id="element-667" data-mp="2jyzqhh9hgttt">
        <h3 className="font-bold text-xl" data-id="element-668" data-mp="2k0j758z1x6w4">TEAMMATES</h3>
        <div className="flex items-center space-x-3" data-id="element-669" data-mp="2oy2le8vr1jo2">
          <div className="text-sm text-gray-600" data-id="element-670" data-mp="2hdi6obe5g55s">
            Found {filteredTeammates.length} teammates
          </div>
        </div>
      </div>

      {/* Filters - Always visible and reorganized */}
      <div className="mb-6 w-full" data-id="element-671" data-mp="27np768ra7ghw">
        {/* Search Row */}
        <div className="flex flex-wrap gap-3 mb-4 items-center w-full" data-id="element-672" data-mp="27o8e6ufe8kh5">
          {/* Search */}
          <div className="relative flex-grow max-w-md" data-id="element-673" data-mp="22qp0pwqisbq9">
            <input type="text" placeholder="Search teammates..." className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm" value={filters.search} onChange={e => handleFilterChange('search', e.target.value)} data-id="element-674" data-mp="30cw6vb2h2c4q" />
            <SearchIcon className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" data-id="element-675" data-mp="2lg52sohusc8s" />
          </div>
        </div>

        {/* Primary Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4 w-full" data-id="element-676" data-mp="1ex6ucg0ra0ra">
          {/* Season Filter */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-677" data-mp="20rggvepfe94">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-678" data-mp="1yn3lnytv4wp">
              Season
            </label>
            <div className="flex flex-wrap gap-1.5" data-id="element-679" data-mp="3o7y4fpto5x4u">
              {allSeasons.map(season => <button key={season} onClick={() => toggleFilterItem('seasons', season)} className={`text-xs px-2 py-1 rounded-md ${filters.seasons.includes(season) ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`} data-id="element-680" data-mp="3t72c2c0y3mt2">
                  {season}
                </button>)}
            </div>
          </div>

          {/* Team Filter */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-681" data-mp="3afrrecltc69m">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-682" data-mp="2vjk77caen4uc">
              Team
            </label>
            <select className="w-full p-2 border rounded-md text-sm bg-white" value={filters.teams[0] || ''} onChange={e => handleFilterChange('teams', e.target.value ? [e.target.value] : [])} data-id="element-683" data-mp="30in1q2srdso2">
              <option value="" data-id="element-684" data-mp="2qw2knwiz6kaj">All Teams</option>
              {allTeams.map(team => <option key={team} value={team} data-id="element-685" data-mp="2lu3i2ov27ren">
                  {team}
                </option>)}
            </select>
          </div>

          {/* Position Filter */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-686" data-mp="2vt2r48jqlp26">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-687" data-mp="39limqoab6kt2">
              Position
            </label>
            <select className="w-full p-2 border rounded-md text-sm bg-white" value={filters.positions[0] || ''} onChange={e => handleFilterChange('positions', e.target.value ? [e.target.value] : [])} data-id="element-688" data-mp="34nh4u6omcrj6">
              <option value="" data-id="element-689" data-mp="24745rswdq0v3">All Positions</option>
              {allPositions.map(position => <option key={position} value={position} data-id="element-690" data-mp="2967atdcd0732">
                  {position}
                </option>)}
            </select>
          </div>

          {/* Nationality Filter */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-691" data-mp="1ghjnlc81v7mv">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-692" data-mp="1gff960ciyq8c">
              Nationality
            </label>
            <select className="w-full p-2 border rounded-md text-sm bg-white" value={filters.nationalities[0] || ''} onChange={e => handleFilterChange('nationalities', e.target.value ? [e.target.value] : [])} data-id="element-693" data-mp="1qfzdxcz96cvb">
              <option value="" data-id="element-694" data-mp="12ok8hbc6jffu">All Nationalities</option>
              {allNationalities.map(nationality => <option key={nationality} value={nationality} data-id="element-695" data-mp="3fyaj7tjskmf1">
                  {nationality}
                </option>)}
            </select>
          </div>

          {/* Competition Filter */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-696" data-mp="371jeksr6bz8u">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-697" data-mp="3203w98gds6u5">
              Competition
            </label>
            <select className="w-full p-2 border rounded-md text-sm bg-white" value={filters.competitions[0] || ''} onChange={e => handleFilterChange('competitions', e.target.value ? [e.target.value] : [])} data-id="element-698" data-mp="2x3dfsk0nz987">
              <option value="" data-id="element-699" data-mp="2e9zsn1370735">All Competitions</option>
              {allCompetitions.map(competition => <option key={competition} value={competition} data-id="element-700" data-mp="29chqpunles4n">
                  {competition}
                </option>)}
            </select>
          </div>
        </div>

        {/* Minutes Range Filter */}
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 w-full" data-id="element-701" data-mp="232ffmzwl2ayu">
          <div className="flex justify-between items-center mb-1.5" data-id="element-702" data-mp="1y3uixagji9f5">
            <label className="text-sm font-medium text-gray-700" data-id="element-703" data-mp="1u4lpk6pewdk4">
              Minutes Played Together
            </label>
            <span className="text-xs text-gray-500" data-id="element-704" data-mp="1p6bpbhotymrn">
              {formatMinutes(filters.minutesRange[0])} -{' '}
              {formatMinutes(filters.minutesRange[1])}
            </span>
          </div>
          <div className="px-2" data-id="element-705" data-mp="1ffh6knasm5ay">
            <input type="range" min={0} max={maxMinutes} value={filters.minutesRange[1]} onChange={e => handleFilterChange('minutesRange', [filters.minutesRange[0], parseInt(e.target.value)])} className="w-full accent-blue-600" data-id="element-706" data-mp="1afus6aj8aj0k" />
          </div>
        </div>

        {/* Active Filters */}
        {(filters.seasons.length > 0 || filters.teams.length > 0 || filters.positions.length > 0 || filters.nationalities.length > 0 || filters.competitions.length > 0) && <div className="mt-4 pt-3 border-t w-full" data-id="element-707" data-mp="3qzx0nsobmykr">
            <div className="flex items-center mb-2" data-id="element-708" data-mp="2udbl4fuy2wwy">
              <span className="text-sm font-medium text-gray-700 mr-2" data-id="element-709" data-mp="2ydlsnwydxvdx">
                Active Filters:
              </span>
              <button onClick={() => setFilters({
            ...filters,
            seasons: [],
            teams: [],
            positions: [],
            nationalities: [],
            competitions: []
          })} className="text-xs text-blue-600 hover:underline" data-id="element-710" data-mp="33cgihiursk7j">
                Clear All
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-711" data-mp="3dz6viygp3t8f">
              {filters.seasons.map(season => <div key={season} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full flex items-center" data-id="element-712" data-mp="3rq3m6jr2a0cu">
                  {season}
                  <XIcon className="w-3 h-3 ml-1 cursor-pointer" onClick={() => toggleFilterItem('seasons', season)} data-id="element-713" data-mp="2zynqm07j13ah" />
                </div>)}
              {filters.teams.map(team => <div key={team} className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full flex items-center" data-id="element-714" data-mp="38xr9p46p1ioz">
                  {team}
                  <XIcon className="w-3 h-3 ml-1 cursor-pointer" onClick={() => toggleFilterItem('teams', team)} data-id="element-715" data-mp="obpr3y2tkxep" />
                </div>)}
              {filters.positions.map(position => <div key={position} className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center" data-id="element-716" data-mp="vtxe4qov0n6q">
                  {position}
                  <XIcon className="w-3 h-3 ml-1 cursor-pointer" onClick={() => toggleFilterItem('positions', position)} data-id="element-717" data-mp="msziowshlnzr" />
                </div>)}
              {filters.nationalities.map(nationality => <div key={nationality} className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full flex items-center" data-id="element-718" data-mp="10r7civu8896p">
                  {nationality}
                  <XIcon className="w-3 h-3 ml-1 cursor-pointer" onClick={() => toggleFilterItem('nationalities', nationality)} data-id="element-719" data-mp="1tfbjdkojasaz" />
                </div>)}
              {filters.competitions.map(competition => <div key={competition} className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full flex items-center" data-id="element-720" data-mp="5mksfym2xo38">
                  {competition}
                  <XIcon className="w-3 h-3 ml-1 cursor-pointer" onClick={() => toggleFilterItem('competitions', competition)} data-id="element-721" data-mp="t9swkd7rur3o" />
                </div>)}
            </div>
          </div>}
      </div>

      {/* Teammates Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg w-full" data-id="element-722" data-mp="ek3jbjmemwn1">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-723" data-mp="1c0k0v0cwjrb7">
          <thead className="bg-gray-50" data-id="element-724" data-mp="179k6a1r99lbv">
            <tr data-id="element-725" data-mp="178ishpiuukqg">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortByColumn('alphabetical')} data-id="element-726" data-mp="177zdohoi4cgn">
                <div className="flex items-center" data-id="element-727" data-mp="1oj61dqjbbj8n">
                  Player
                  {getSortIndicator('alphabetical')}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-728" data-mp="1tfwfw9fgy6ry">
                Position
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-729" data-mp="279dwblcuz3dr">
                Teams
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortByColumn('seasons')} data-id="element-730" data-mp="2h7tqjx76msrh">
                <div className="flex items-center" data-id="element-731" data-mp="m4wz4i435b5b">
                  Seasons
                  {getSortIndicator('seasons')}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortByColumn('minutes')} data-id="element-732" data-mp="ux8lnsw2weg0">
                <div className="flex items-center" data-id="element-733" data-mp="14t20xk16mm1h">
                  Minutes Together
                  {getSortIndicator('minutes')}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-734" data-mp="1du9x9wk51csx">
                Competitions
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-735" data-mp="3fn7us5ov0tk7">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-736" data-mp="35irfbdfalegx">
            {filteredTeammates.map(teammate => <tr key={teammate.id} className={`hover:bg-gray-50 ${teammate.minutesPlayed > 3000 ? 'bg-blue-50' : ''}`} data-id="element-737" data-mp="2rse7db2ehzrl">
                {/* Player */}
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-738" data-mp="2ht5eymn6ot7c">
                  <div className="flex items-center" data-id="element-739" data-mp="2msi3dbtpe8c1">
                    <div className="flex-shrink-0 h-10 w-10 relative" data-id="element-740" data-mp="2mtb0rm6rvlhi">
                      <img className="h-10 w-10 rounded-full object-cover" src={teammate.photo} alt={teammate.name} data-id="element-741" data-mp="28ta4kk1gd28d" />
                      <img src={teammate.nationalityFlag} alt={teammate.nationality} className="absolute bottom-0 right-0 w-4 h-4 rounded-full border border-white" data-id="element-742" data-mp="72f259cqq4so" />
                    </div>
                    <div className="ml-4" data-id="element-743" data-mp="1flaycy81527c">
                      <div className="text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600" data-id="element-744" data-mp="1fmkw1maz08dg">
                        {teammate.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-745" data-mp="16spqid5xsfxa">
                        {teammate.nationality}
                      </div>
                    </div>
                  </div>
                </td>
                {/* Position */}
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-746" data-mp="wxo25jjhjhvr">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPositionColor(teammate.position)}`} data-id="element-747" data-mp="rx0082tpxgmr">
                    {teammate.position}
                  </span>
                </td>
                {/* Teams */}
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-748" data-mp="2i9z9kyl5l5y2">
                  <div className="flex flex-col space-y-1" data-id="element-749" data-mp="41506zalhsab">
                    {teammate.teams.map((team, index) => <div key={index} className="flex items-center" data-id="element-750" data-mp="8z6obymvohsn" data-mpi={index}>
                        <img src={team.logo} alt={team.name} className="h-6 w-6 mr-2" data-id="element-751" data-mp="dqydc4qdyfgo" />
                        <span className="text-xs" data-id="element-752" data-mp="3h7ex9741n5hy">{team.name}</span>
                      </div>)}
                  </div>
                </td>
                {/* Seasons */}
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-753" data-mp="3m8coe97179vl">
                  <div className="text-sm text-gray-900" data-id="element-754" data-mp="3q7bnve9zuefn">
                    {teammate.seasonsShared.length > 0 ? <span data-id="element-755" data-mp="3v8hijhn7sbrt">
                        {teammate.seasonsShared[0]} -{' '}
                        {teammate.seasonsShared[teammate.seasonsShared.length - 1]}
                      </span> : <span data-id="element-756" data-mp="26hlh76s7dgiz">-</span>}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-757" data-mp="26jo59l9sozl2">
                    {teammate.seasonsShared.length} season
                    {teammate.seasonsShared.length !== 1 ? 's' : ''}
                  </div>
                </td>
                {/* Minutes Together */}
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-758" data-mp="1iqplhykmm16g">
                  <div className="text-sm text-gray-900" data-id="element-759" data-mp="1mu4c1j66xzy5">
                    {formatMinutes(teammate.minutesPlayed)}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1" data-id="element-760" data-mp="18vxjmik0gs5v">
                    <div className="bg-blue-600 h-1.5 rounded-full" style={{
                  width: `${teammate.minutesPlayed / maxMinutes * 100}%`
                }} data-id="element-761" data-mp="1dtz5fb8tn5l1"></div>
                  </div>
                </td>
                {/* Competitions */}
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-762" data-mp="whiru0hb9wxh">
                  <div className="flex space-x-1" data-id="element-763" data-mp="rm3ed9n7u31l">
                    {teammate.competitions.map((competition, index) => <div key={index} className="flex items-center" title={competition.name} data-id="element-764" data-mp="16brh8fb1p9rf" data-mpi={index}>
                        <img src={competition.logo} alt={competition.name} className="h-5 w-5" data-id="element-765" data-mp="1f8ibqn9y8gjw" />
                      </div>)}
                  </div>
                </td>
                {/* Actions */}
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-766" data-mp="1k7ldnyumu6nn">
                  <div className="flex space-x-2" data-id="element-767" data-mp="3r201xao4jths">
                    <button className="text-blue-600 hover:text-blue-900 bg-blue-100 p-1.5 rounded-full" title="Compare similarity" data-id="element-768" data-mp="3m1ksgu6yr653">
                      <UserIcon className="w-4 h-4" data-id="element-769" data-mp="jkdy44r2vgrg" />
                    </button>
                    <button className="text-red-600 hover:text-red-900 bg-red-100 p-1.5 rounded-full" title="Check affinity" data-id="element-770" data-mp="okidtsk5w7gm">
                      <HeartIcon className="w-4 h-4" data-id="element-771" data-mp="yiofqa5yxdlh" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
};
export default PlayerTeammates;